import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FileText,
  Image,
  Download,
  Shield,
  ShieldCheck,
  CheckCircle,
  User,
  Plus,
  Eye,
  Pill,
  X,
  ExternalLink,
} from 'lucide-react';
import { apiClient } from '@/api/axios';
import { toast } from 'sonner';
import { UploadDocumentModal } from '../documents/UploadDocumentModal';
import { QRCodeCanvas } from '../shared/QRCodeCanvas';

interface Document {
  id: string;
  // 'file' = R2-stored upload (downloadable),
  // 'certificate' = vet-issued cert (viewable inline),
  // 'prescription' = vet-issued prescription (viewable inline).
  kind: 'file' | 'certificate' | 'prescription';
  name: string;
  category: string;
  uploadDate: string;
  uploadedBy: string;
  uploadedByType: 'owner' | 'professional';
  fileType: string;
  fileSize: number;
  verified: boolean;
  onChainHash?: string;
  issuer?: string;
  notes?: string;
  // Raw payload for certificate / prescription kinds — used by the inline
  // viewer modals so we don't need a second round-trip on click.
  payload?: any;
}

interface DocumentTimelineProps {
  petId: string;
}

function decodeBase64UrlToUtf8(base64url: string): string {
  let base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  while (base64.length % 4) base64 += '=';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new TextDecoder('utf-8').decode(bytes);
}

function parseKeyForDisplay(key: string): {
  displayName: string;
  fileName: string;
} {
  const pathParts = key.split('/');
  const lastPart = pathParts[pathParts.length - 1] || '';
  const segs = lastPart.split('__');
  if (segs.length >= 3) {
    try {
      const base64url = segs[1];
      const fileName = segs.slice(2).join('__');
      const displayName = decodeBase64UrlToUtf8(base64url);
      if (displayName && fileName) return { displayName, fileName };
    } catch {
      /* fall through */
    }
  }
  const originalNameIdx = lastPart.indexOf('_');
  const fileName =
    originalNameIdx >= 0 ? lastPart.substring(originalNameIdx + 1) : lastPart;
  return { displayName: fileName, fileName };
}

const CATEGORY_LABEL_KEYS: Record<string, string> = {
  'vaccination-record': 'documents.typeVaccinationCertificate',
  'health-certificate': 'documents.typeHealthCertificate',
  'lab-result': 'documents.typeLabResults',
  'x-ray-scan': 'documents.typeXrayImaging',
  'medical-record': 'documents.typeMedicalRecord',
  'insurance-document': 'documents.typeInsurance',
  grooming: 'documents.typeGroomingReport',
  other: 'documents.typeOther',
};

function getCategoryLabel(
  type: string,
  t: (key: string, defaultValue?: string) => string
): string {
  if (CATEGORY_LABEL_KEYS[type]) {
    return t(CATEGORY_LABEL_KEYS[type]);
  }
  const fallback =
    type
      .split('-')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ') || t('documents.typeOther', 'Other');
  return fallback;
}

export function DocumentTimeline({ petId }: DocumentTimelineProps) {
  const { t } = useTranslation();
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterVerification, setFilterVerification] = useState('all');
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [viewingCert, setViewingCert] = useState<any>(null);
  const [viewingRx, setViewingRx] = useState<any>(null);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      // Fetch R2 uploads, vet-issued certificates, and vet-issued
      // prescriptions in parallel. The timeline merges all three so the
      // owner sees a single chronological view of every paper trail item
      // associated with their pet, regardless of which side originated it.
      const [docRes, certRes, rxRes] = await Promise.all([
        apiClient.get<{
          items: {
            key?: string | null;
            name?: string | null;
            size?: number | null;
            lastModified?: string | Date | null;
          }[];
        }>('/documents'),
        apiClient
          .get<{ certificates: any[] }>(`/pets/${petId}/certificates`)
          .catch(() => ({ data: { certificates: [] } })),
        apiClient
          .get<{ prescriptions: any[] }>(`/pets/${petId}/prescriptions`)
          .catch(() => ({ data: { prescriptions: [] } })),
      ]);
      const items = docRes.data.items || [];
      const certList = certRes.data.certificates || [];
      const rxList = rxRes.data.prescriptions || [];
      const looksLikeSocial = (value: string): boolean => {
        const k = value.replace(/\\/g, '/').trim().toLowerCase();
        if (
          k.startsWith('social_') ||
          k.includes('/social_') ||
          k.includes('/social/')
        )
          return true;
        const parts = value.split('/');
        const lastSegment = (parts[parts.length - 1] || '').toLowerCase();
        if (lastSegment.startsWith('social_')) return true;
        if (lastSegment.includes('_social_')) return true;
        return parts.some(
          (p) =>
            p.toLowerCase() === 'social' ||
            p.toLowerCase().startsWith('social_')
        );
      };
      const documentItems = items.filter((i) => {
        if (!i.key || typeof i.key !== 'string') return false;
        if (i.key.includes('/pets/')) return false;
        if (looksLikeSocial(i.key)) return false;
        if (i.name && typeof i.name === 'string' && looksLikeSocial(i.name))
          return false;
        return true;
      });
      const mapped: Document[] = documentItems
        .filter((i) => i.key)
        .map((i) => {
          const key = i.key as string;
          const pathParts = key.split('/');
          const type = pathParts.length > 1 ? pathParts[1] : 'other';
          const { displayName, fileName } = parseKeyForDisplay(key);
          const lastModified = i.lastModified
            ? new Date(i.lastModified as string)
            : new Date();
          return {
            id: key,
            kind: 'file' as const,
            name:
              displayName ||
              fileName ||
              t('documents.unnamedDocument', 'Document'),
            category: getCategoryLabel(type, t),
            uploadDate: lastModified.toISOString(),
            uploadedBy: t('common.owner', 'Owner'),
            uploadedByType: 'owner' as const,
            fileType: fileName?.toLowerCase().endsWith('.pdf')
              ? 'application/pdf'
              : 'image/jpeg',
            fileSize: (i.size as number) || 0,
            verified: false,
          };
        });
      // Exclude any that display as social (display name starts with social_)
      const filteredMapped = mapped.filter((doc) => {
        const n = (doc.name || '').toLowerCase();
        const id = (doc.id || '').toLowerCase();
        return !n.startsWith('social_') && !id.startsWith('social_');
      });

      // Map vet-issued certificates into Document items. The cert is its
      // own paper trail entry — verified by definition (issued + signed),
      // and the cert payload becomes the inline viewer's source.
      const certDocs: Document[] = certList.map((c: any) => ({
        id: `cert:${c.id}`,
        kind: 'certificate' as const,
        name:
          c.title ||
          (c.certificateType
            ? `${String(c.certificateType).replace(/-/g, ' ')} certificate`
            : 'Certificate'),
        category: t(
          'documents.typeProfessionalCertificate',
          'Professional Certificate',
        ),
        uploadDate: c.issuedAt || c.createdAt || new Date().toISOString(),
        uploadedBy:
          [c.issuedByTitle, c.issuedByName].filter(Boolean).join(' ') ||
          c.businessName ||
          t('documents.uploadedByVet', 'Veterinarian'),
        uploadedByType: 'professional' as const,
        fileType: 'application/pdf',
        fileSize: 0,
        verified: true,
        onChainHash: c.documentHash || undefined,
        issuer: c.businessName || undefined,
        payload: c,
      }));

      // Map vet-issued prescriptions into Document items.
      const rxDocs: Document[] = rxList.map((r: any) => {
        const meds =
          typeof r.medications === 'string'
            ? (() => {
                try {
                  return JSON.parse(r.medications);
                } catch {
                  return [];
                }
              })()
            : r.medications || [];
        return {
          id: `rx:${r.id}`,
          kind: 'prescription' as const,
          name:
            r.prescriptionNumber || r.diagnosis || 'Prescription',
          category: t('documents.typePrescription', 'Prescription'),
          uploadDate: r.issuedAt || r.createdAt || new Date().toISOString(),
          uploadedBy:
            [r.issuedByTitle, r.signerName].filter(Boolean).join(' ') ||
            r.businessName ||
            t('documents.uploadedByVet', 'Veterinarian'),
          uploadedByType: 'professional' as const,
          fileType: 'application/pdf',
          fileSize: 0,
          verified: true,
          issuer: r.businessName || undefined,
          payload: { ...r, medications: meds },
        };
      });

      const merged = [...filteredMapped, ...certDocs, ...rxDocs];
      merged.sort(
        (a, b) =>
          new Date(b.uploadDate).getTime() - new Date(a.uploadDate).getTime()
      );
      setDocuments(merged);
    } catch (e) {
      console.error('Failed to fetch documents', e);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [petId]);

  const categories = [
    'all',
    ...Array.from(new Set(documents.map((d) => d.category))).sort(),
  ];

  const filteredDocuments = documents.filter((doc) => {
    if (filterCategory !== 'all' && doc.category !== filterCategory)
      return false;
    if (filterVerification === 'verified' && !doc.verified) return false;
    if (filterVerification === 'unverified' && doc.verified) return false;
    return true;
  });

  const getFileIcon = (doc: Document) => {
    if (doc.kind === 'certificate')
      return <ShieldCheck className="w-5 h-5 text-green-600 dark:text-green-400" />;
    if (doc.kind === 'prescription')
      return <Pill className="w-5 h-5 text-purple-600 dark:text-purple-400" />;
    if (doc.fileType.startsWith('image/'))
      return <Image className="w-5 h-5 text-blue-600 dark:text-blue-400" />;
    return <FileText className="w-5 h-5 text-red-600 dark:text-red-400" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getVerificationBadge = (doc: Document) => {
    if (doc.onChainHash) {
      return (
        <span
          className="flex items-center gap-1 text-xs text-purple-600 dark:text-purple-400 px-2 py-1 bg-purple-50 dark:bg-purple-900/30 rounded"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          <Shield className="w-3 h-3" />
          {t('documents.badgeOnChain', 'On-chain certified')}
        </span>
      );
    }
    if (doc.verified) {
      return (
        <span
          className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400 px-2 py-1 bg-green-50 dark:bg-green-900/30 rounded"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          <CheckCircle className="w-3 h-3" />
          {t('documents.badgeProfessionalVerified', 'Verified by professional')}
        </span>
      );
    }
    return (
      <span
        className="flex items-center gap-1 text-xs text-gray-600 dark:text-white/70 px-2 py-1 bg-gray-50 dark:bg-white/10 rounded"
        style={{ fontFamily: 'Inter, sans-serif' }}
      >
        <User className="w-3 h-3" />
        {t('documents.badgeOwnerUploaded', 'Uploaded by owner')}
      </span>
    );
  };

  const handleDownload = async (doc: Document) => {
    try {
      const encodedKey = encodeURIComponent(doc.id);
      const res = await apiClient.get<{ url: string; filename?: string }>(
        `/documents/${encodedKey}/download`,
        { params: { disposition: 'attachment' } }
      );
      const a = document.createElement('a');
      a.href = res.data.url;
      a.download = res.data.filename || doc.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success(t('documents.downloadStarted', 'Download started'));
    } catch (err) {
      console.error('Download failed', err);
      toast.error(t('documents.downloadFailed', 'Download failed'));
    }
  };

  const handleUploadComplete = () => {
    setShowUploadModal(false);
    fetchDocuments();
    toast.success(t('documents.documentUploaded'));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3
            className="text-xl mb-1 text-gray-900 dark:text-white"
            style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
          >
            {t('documents.documentTimelineTitle')}
          </h3>
          <p
            className="text-sm text-gray-600 dark:text-white/70"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {t('documents.documentTimelineSubtitle')}
          </p>
        </div>
        <button
          onClick={() => setShowUploadModal(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-white hover:opacity-90 transition-opacity"
          style={{
            background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 600,
          }}
        >
          <Plus className="w-4 h-4" />
          {t('documents.upload')}
        </button>
      </div>

      {loading ? (
        <div
          className="text-center py-12 text-gray-500 dark:text-white/50"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          {t('documents.loadingDocuments')}
        </div>
      ) : documents.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-[#0F0E16] rounded-xl border border-gray-200 dark:border-white/8">
          <FileText className="w-16 h-16 text-gray-300 dark:text-white/20 mx-auto mb-4" />
          <p
            className="text-gray-600 dark:text-white/70 mb-2"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {t('documents.noDocuments')}
          </p>
          <p
            className="text-sm text-gray-500 dark:text-white/50 mb-4"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {t('documents.noDocumentsGetStarted')}
          </p>
          <button
            onClick={() => setShowUploadModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white hover:opacity-90 transition-opacity"
            style={{
              background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
            }}
          >
            <Plus className="w-4 h-4" />
            {t('documents.upload')}
          </button>
        </div>
      ) : (
        <>
          {/* Filters */}
          <div className="flex flex-wrap gap-4">
            <div>
              <label
                className="block text-sm text-gray-600 dark:text-white/70 mb-2"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('documents.filterByCategory')}
              </label>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat === 'all' ? t('documents.allCategories') : cat}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                className="block text-sm text-gray-600 dark:text-white/70 mb-2"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('documents.filterByVerification')}
              </label>
              <select
                value={filterVerification}
                onChange={(e) => setFilterVerification(e.target.value)}
                className="px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                <option value="all">{t('documents.allDocuments')}</option>
                <option value="verified">{t('documents.verifiedOnly')}</option>
                <option value="unverified">
                  {t('documents.unverifiedOnly')}
                </option>
              </select>
            </div>
          </div>

          {/* Statistics */}
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-white dark:bg-[#0F0E16] border border-gray-200 dark:border-white/8 rounded-lg p-4">
              <div
                className="text-sm text-gray-600 dark:text-white/50 mb-1"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('documents.totalDocuments')}
              </div>
              <div
                className="text-2xl text-gray-900 dark:text-white"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {documents.length}
              </div>
            </div>
            <div className="bg-white dark:bg-[#0F0E16] border border-gray-200 dark:border-white/8 rounded-lg p-4">
              <div
                className="text-sm text-gray-600 dark:text-white/50 mb-1"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('documents.verified')}
              </div>
              <div
                className="text-2xl text-green-600 dark:text-green-400"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {documents.filter((d) => d.verified || d.onChainHash).length}
              </div>
            </div>
            <div className="bg-white dark:bg-[#0F0E16] border border-gray-200 dark:border-white/8 rounded-lg p-4">
              <div
                className="text-sm text-gray-600 dark:text-white/50 mb-1"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('documents.ownerUploaded')}
              </div>
              <div
                className="text-2xl text-blue-600 dark:text-blue-400"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {documents.filter((d) => !d.verified && !d.onChainHash).length}
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className="relative">
            <div className="absolute left-16 top-0 bottom-0 w-0.5 bg-gray-200 dark:bg-white/10" />
            <div className="space-y-6">
              {filteredDocuments.map((doc) => (
                <div key={doc.id} className="relative pl-24">
                  <div className="absolute left-[59px] top-6 w-4 h-4 rounded-full bg-blue-600 dark:bg-blue-500 border-4 border-white dark:border-[#16151D]" />
                  <div
                    className="absolute left-0 top-5 text-xs text-gray-500 dark:text-white/50 w-12"
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      lineHeight: '1.2',
                    }}
                  >
                    {new Date(doc.uploadDate).toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                    })}
                  </div>
                  <div className="bg-white dark:bg-[#0F0E16] border border-gray-200 dark:border-white/8 rounded-lg p-4 hover:shadow-md dark:hover:bg-[#1A1921] transition-shadow">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        {getFileIcon(doc)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4 mb-2">
                          <div className="flex-1 min-w-0">
                            <h4
                              className="truncate mb-1 text-gray-900 dark:text-white"
                              style={{
                                fontFamily: 'Sora, sans-serif',
                                fontWeight: 600,
                              }}
                            >
                              {doc.name}
                            </h4>
                            <div
                              className="flex items-center gap-2 text-sm text-gray-600 dark:text-white/70 mb-1"
                              style={{ fontFamily: 'Inter, sans-serif' }}
                            >
                              <span className="px-2 py-0.5 bg-gray-100 dark:bg-white/10 rounded text-xs">
                                {doc.category}
                              </span>
                              {doc.kind === 'file' && doc.fileSize > 0 && (
                                <>
                                  <span>•</span>
                                  <span>{formatFileSize(doc.fileSize)}</span>
                                </>
                              )}
                            </div>
                          </div>
                          {getVerificationBadge(doc)}
                        </div>
                        <div
                          className="text-xs text-gray-500 dark:text-white/50 mb-2"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          {t('documents.uploadedBy')} {doc.uploadedBy} •{' '}
                          {new Date(doc.uploadDate).toLocaleString()}
                        </div>
                        <div className="flex gap-2 mt-3">
                          {doc.kind === 'file' ? (
                            <button
                              className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-white/20 rounded hover:bg-gray-50 dark:hover:bg-white/5 transition-colors text-gray-700 dark:text-white/90"
                              style={{ fontFamily: 'Inter, sans-serif' }}
                              onClick={() => handleDownload(doc)}
                            >
                              <Download className="w-4 h-4" />
                              {t('documents.download')}
                            </button>
                          ) : (
                            <button
                              className="flex items-center gap-1 px-3 py-1.5 text-sm border border-green-300 dark:border-green-700 rounded hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors text-green-700 dark:text-green-400"
                              style={{ fontFamily: 'Inter, sans-serif' }}
                              onClick={() => {
                                if (doc.kind === 'certificate') setViewingCert(doc.payload);
                                else if (doc.kind === 'prescription') setViewingRx(doc.payload);
                              }}
                            >
                              <Eye className="w-4 h-4" />
                              {t('documents.view', 'View')}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {filteredDocuments.length === 0 && (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-gray-300 dark:text-white/20 mx-auto mb-4" />
              <p
                className="text-gray-500 dark:text-white/50"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('documents.noDocumentsMatching')}
              </p>
            </div>
          )}

          {/* Info Box */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border border-blue-200 dark:border-blue-500/30 rounded-lg p-6">
            <h4
              className="mb-3 text-gray-900 dark:text-white"
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
            >
              {t(
                'documents.verificationBadgesTitle',
                'Verification badge meanings'
              )}
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-start gap-3">
                <Shield className="w-4 h-4 text-purple-600 dark:text-purple-400 mt-0.5" />
                <div style={{ fontFamily: 'Inter, sans-serif' }}>
                  <strong className="text-purple-600 dark:text-purple-400">
                    {t('documents.onChainTitle', 'On-chain certified:')}
                  </strong>{' '}
                  <span className="text-gray-700 dark:text-blue-100">
                    {t(
                      'documents.onChainDesc',
                      'Document hash is stored on-chain and can be independently verified.'
                    )}
                  </span>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5" />
                <div style={{ fontFamily: 'Inter, sans-serif' }}>
                  <strong className="text-green-600 dark:text-green-400">
                    {t(
                      'documents.professionalVerifiedTitle',
                      'Professionally verified:'
                    )}
                  </strong>{' '}
                  <span className="text-gray-700 dark:text-blue-100">
                    {t(
                      'documents.professionalVerifiedDesc',
                      'Uploaded by a verified professional (vet, groomer, etc.).'
                    )}
                  </span>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <User className="w-4 h-4 text-gray-600 dark:text-white/70 mt-0.5" />
                <div style={{ fontFamily: 'Inter, sans-serif' }}>
                  <strong className="text-gray-600 dark:text-white/90">
                    {t('documents.ownerUploadedTitle', 'Owner uploaded:')}
                  </strong>{' '}
                  <span className="text-gray-700 dark:text-blue-100">
                    {t(
                      'documents.ownerUploadedDesc',
                      'Uploaded by the owner and can be verified by a professional later.'
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {showUploadModal && (
        <UploadDocumentModal
          onClose={() => setShowUploadModal(false)}
          onUpload={handleUploadComplete}
        />
      )}

      {/* Inline Certificate Viewer — opened from a 'certificate'-kind row */}
      {viewingCert && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem',
            backgroundColor: 'rgba(0,0,0,0.5)',
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setViewingCert(null);
          }}
        >
          <div
            className="bg-white dark:bg-[#16151D] rounded-2xl shadow-2xl"
            style={{
              maxWidth: '28rem',
              width: '100%',
              maxHeight: '85vh',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div
              style={{
                background: 'linear-gradient(to right, #059669, #047857)',
                color: 'white',
                padding: '1.5rem',
                borderRadius: '1rem 1rem 0 0',
                flexShrink: 0,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ fontSize: '1.125rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <ShieldCheck style={{ width: '20px', height: '20px' }} />
                  {viewingCert.title || 'Certificate'}
                </h2>
                <button
                  type="button"
                  onClick={() => setViewingCert(null)}
                  style={{ color: 'rgba(255,255,255,0.8)', cursor: 'pointer', background: 'none', border: 'none' }}
                >
                  <X style={{ width: '20px', height: '20px' }} />
                </button>
              </div>
              <p style={{ fontSize: '0.75rem', opacity: 0.8, marginTop: '4px' }}>{viewingCert.certificateNumber}</p>
            </div>
            <div style={{ padding: '1.5rem', flex: 1, overflowY: 'auto', minHeight: 0 }}>
              <div style={{ width: '100%', background: '#fff', borderRadius: '1rem', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #f1f5f9', overflow: 'hidden' }}>
                <div style={{ background: '#0f172a', padding: '16px', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <p style={{ fontSize: '9px', color: '#94a3b8', textTransform: 'uppercase' }}>Issued by</p>
                    <p style={{ fontSize: '13px', fontWeight: 600, marginTop: '2px' }}>
                      {viewingCert.issuedByTitle ? `${viewingCert.issuedByTitle} ` : ''}
                      {viewingCert.issuedByName || 'Professional'}
                    </p>
                    {viewingCert.businessName && (
                      <p style={{ fontSize: '10px', color: '#94a3b8', marginTop: '2px' }}>{viewingCert.businessName}</p>
                    )}
                  </div>
                  <ShieldCheck style={{ color: '#34d399', width: '22px', height: '22px' }} />
                </div>
                <div style={{ background: '#f8fafc', padding: '10px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between' }}>
                  <div>
                    <p style={{ fontSize: '9px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Issued To</p>
                    <p style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>{viewingCert.petName}</p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ fontSize: '9px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Document</p>
                    <p style={{ fontSize: '11px', fontWeight: 500, color: '#0f172a' }}>{viewingCert.title}</p>
                  </div>
                </div>
                {viewingCert.data && (() => {
                  const d =
                    typeof viewingCert.data === 'string'
                      ? (() => {
                          try {
                            return JSON.parse(viewingCert.data);
                          } catch {
                            return {};
                          }
                        })()
                      : viewingCert.data;
                  return (
                    <div style={{ padding: '12px 16px' }}>
                      {Object.entries(d)
                        .filter(
                          ([k, v]) =>
                            v &&
                            ![
                              'certificateId',
                              'certificateNumber',
                              'certificateType',
                              'title',
                              'issuedAt',
                              'issuedByName',
                              'issuedByTitle',
                              'documentHash',
                              'systems',
                            ].includes(k),
                        )
                        .map(([key, value]) => (
                          <div
                            key={key}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              fontSize: '11px',
                              borderBottom: '1px solid #f1f5f9',
                              padding: '4px 0',
                            }}
                          >
                            <span style={{ color: '#64748b', textTransform: 'capitalize' }}>
                              {key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()}
                            </span>
                            <span style={{ fontWeight: 500, color: '#1e293b' }}>{String(value)}</span>
                          </div>
                        ))}
                    </div>
                  );
                })()}
                <div style={{ padding: '8px 16px', borderTop: '1px solid #e2e8f0', fontSize: '11px' }}>
                  {viewingCert.certificateNumber && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                      <span style={{ color: '#64748b' }}>Certificate No.</span>
                      <span style={{ fontWeight: 600, color: '#0f172a', fontFamily: 'monospace', fontSize: '10px' }}>
                        {viewingCert.certificateNumber}
                      </span>
                    </div>
                  )}
                  {viewingCert.issuedAt && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                      <span style={{ color: '#64748b' }}>Issued</span>
                      <span style={{ fontWeight: 500, color: '#0f172a' }}>
                        {new Date(viewingCert.issuedAt).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  {viewingCert.validFrom && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                      <span style={{ color: '#64748b' }}>Valid From</span>
                      <span style={{ fontWeight: 500, color: '#0f172a' }}>{viewingCert.validFrom}</span>
                    </div>
                  )}
                  {viewingCert.validUntil && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                      <span style={{ color: '#64748b' }}>Valid Until</span>
                      <span style={{ fontWeight: 500, color: '#0f172a' }}>{viewingCert.validUntil}</span>
                    </div>
                  )}
                </div>
                <div
                  style={{
                    background: '#ecfdf5',
                    padding: '12px 16px',
                    borderTop: '1px solid #a7f3d0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div>
                    <p
                      style={{
                        fontSize: '8px',
                        fontWeight: 700,
                        color: '#065f46',
                        textTransform: 'uppercase',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '3px',
                      }}
                    >
                      <ShieldCheck style={{ width: '10px', height: '10px' }} /> Cryptographically Verified
                    </p>
                    <p
                      style={{
                        fontSize: '7px',
                        color: '#047857',
                        fontFamily: 'monospace',
                        marginTop: '4px',
                        wordBreak: 'break-all',
                      }}
                    >
                      {viewingCert.documentHash
                        ? String(viewingCert.documentHash).slice(0, 32) + '...'
                        : viewingCert.certificateNumber}
                    </p>
                  </div>
                  <div style={{ background: '#fff', padding: '4px', borderRadius: '8px', border: '1px solid #a7f3d0' }}>
                    <QRCodeCanvas
                      value={
                        viewingCert.verificationUrl ||
                        `https://api-dev.petso.dev/verify/${viewingCert.certificateNumber}`
                      }
                      size={48}
                    />
                  </div>
                </div>
              </div>
              {viewingCert.verificationUrl && (
                <a
                  href={viewingCert.verificationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 mt-3"
                >
                  <ExternalLink className="w-4 h-4" />
                  Verify certificate publicly
                </a>
              )}
            </div>
            <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #e5e7eb', flexShrink: 0 }}>
              <button
                type="button"
                onClick={() => setViewingCert(null)}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#059669',
                  color: '#fff',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Inline Prescription Viewer — opened from a 'prescription'-kind row */}
      {viewingRx && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem',
            backgroundColor: 'rgba(0,0,0,0.5)',
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setViewingRx(null);
          }}
        >
          <div
            className="bg-white dark:bg-[#16151D] rounded-2xl shadow-2xl"
            style={{
              maxWidth: '32rem',
              width: '100%',
              maxHeight: '85vh',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div
              style={{
                background: 'linear-gradient(to right, #7c3aed, #5b21b6)',
                color: 'white',
                padding: '1.5rem',
                borderRadius: '1rem 1rem 0 0',
                flexShrink: 0,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ fontSize: '1.125rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Pill style={{ width: '20px', height: '20px' }} />
                  Prescription
                </h2>
                <button
                  type="button"
                  onClick={() => setViewingRx(null)}
                  style={{ color: 'rgba(255,255,255,0.8)', cursor: 'pointer', background: 'none', border: 'none' }}
                >
                  <X style={{ width: '20px', height: '20px' }} />
                </button>
              </div>
              <p style={{ fontSize: '0.75rem', opacity: 0.8, marginTop: '4px' }}>{viewingRx.prescriptionNumber}</p>
            </div>
            <div style={{ padding: '1.5rem', flex: 1, overflowY: 'auto', minHeight: 0 }}>
              <div style={{ marginBottom: '12px' }}>
                <p style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>
                  Issued by
                </p>
                <p style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>
                  {viewingRx.issuedByTitle ? `${viewingRx.issuedByTitle} ` : ''}
                  {viewingRx.signerName || 'Professional'}
                </p>
                {viewingRx.businessName && (
                  <p style={{ fontSize: '12px', color: '#64748b' }}>{viewingRx.businessName}</p>
                )}
              </div>

              <div style={{ marginBottom: '12px' }}>
                <p style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>
                  Patient
                </p>
                <p style={{ fontSize: '14px', color: '#0f172a' }}>{viewingRx.petName}</p>
              </div>

              {viewingRx.diagnosis && (
                <div style={{ marginBottom: '12px' }}>
                  <p style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>
                    Diagnosis
                  </p>
                  <p style={{ fontSize: '13px', color: '#0f172a' }}>{viewingRx.diagnosis}</p>
                </div>
              )}

              <div style={{ marginBottom: '12px' }}>
                <p
                  style={{
                    fontSize: '11px',
                    color: '#64748b',
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    marginBottom: '6px',
                  }}
                >
                  Medications
                </p>
                <div
                  style={{
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    overflow: 'hidden',
                  }}
                >
                  {(viewingRx.medications || []).map((m: any, idx: number) => (
                    <div
                      key={idx}
                      style={{
                        padding: '10px 12px',
                        borderBottom:
                          idx < (viewingRx.medications || []).length - 1
                            ? '1px solid #f1f5f9'
                            : 'none',
                      }}
                    >
                      <div style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>{m.name}</div>
                      <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                        {[m.dosage, m.frequency, m.duration].filter(Boolean).join(' · ')}
                        {m.route ? ` · ${m.route}` : ''}
                      </div>
                      {m.instructions && (
                        <div style={{ fontSize: '11px', color: '#475569', marginTop: '4px', fontStyle: 'italic' }}>
                          {m.instructions}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {viewingRx.instructions && (
                <div style={{ marginBottom: '12px' }}>
                  <p style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>
                    Instructions
                  </p>
                  <p style={{ fontSize: '13px', color: '#0f172a' }}>{viewingRx.instructions}</p>
                </div>
              )}

              <div style={{ display: 'flex', gap: '16px', fontSize: '11px', color: '#64748b' }}>
                {viewingRx.issuedAt && (
                  <span>
                    <strong>Issued:</strong>{' '}
                    {new Date(viewingRx.issuedAt).toLocaleDateString()}
                  </span>
                )}
                {viewingRx.validUntil && (
                  <span>
                    <strong>Valid until:</strong>{' '}
                    {new Date(viewingRx.validUntil).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>
            <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #e5e7eb', flexShrink: 0 }}>
              <button
                type="button"
                onClick={() => setViewingRx(null)}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#7c3aed',
                  color: '#fff',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
